import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useApi } from '@/utils/axios'
import { format, parseISO, parse } from 'date-fns'
import { USUARIO_ROTAS, PERFIL_ROTAS } from '@/utils/apiRoutes'

export const useUserStore = defineStore('user', () => {
  const {
    fetch: fetchUsuariosApi,
    post: createUsuarioApi,
    put: updateUsuarioApi,
    del: deleteUsuarioApi,
  } = useApi(USUARIO_ROTAS.USUARIOS)
  const { fetch: fetchPerfisApi } = useApi(PERFIL_ROTAS.PERFIS_USUARIO)
  const data = ref(null)
  const dataUsuariosMeta = ref(null) // {current_page, last_page, per_page, total}
  const usuario = ref(null)
  const perfis = ref([])
  const paginas = ref([])
  const loading = ref(false)
  const error = ref(null)

  async function fetchUsuarios(options = {}) {
    loading.value = true
    error.value = null
    try {
      // defaults: page 1, per_page 100, name ''
      const { page = 1, per_page = 100, name = '' } = options
      const response = await fetchUsuariosApi({ params: { page, per_page, name } })
      data.value = response.data
      dataUsuariosMeta.value = response.meta
    } catch (err) {
      error.value = err.message || 'Erro ao carregar dados'
    } finally {
      loading.value = false
    }
  }

  async function fetchUsuario(id) {
    loading.value = true
    error.value = null
    try {
      const response = await fetchUsuariosApi({ id })
      const user = response.data
      usuario.value = {
        ...user,
        situacao: user.situacao === 1,
        freela: user.freela === 1,
        site_parceiro: user.site_parceiro === 1,
        datanascimento: user.datanascimento
          ? format(parseISO(user.datanascimento), 'dd/MM/yyyy')
          : '',
        dataadmissao: user.dataadmissao ? format(parseISO(user.dataadmissao), 'dd/MM/yyyy') : '',
        datademissao: user.datademissao ? format(parseISO(user.datademissao), 'dd/MM/yyyy') : '',
      }
    } catch (err) {
      error.value = err.message || 'Erro ao carregar dados do usuário'
    } finally {
      loading.value = false
    }
  }

  async function fetchPaginas(id) {
    loading.value = true
    error.value = null
    try {
      const response = await useApi(USUARIO_ROTAS.PAGINAS(id)).fetch()
      paginas.value = response.data
    } catch (err) {
      error.value = err.message || 'Erro ao carregar páginas'
    } finally {
      loading.value = false
    }
  }

  async function fetchPerfis() {
    loading.value = true
    error.value = null
    try {
      const response = await fetchPerfisApi()
      perfis.value = response.data
    } catch (err) {
      error.value = err.message || 'Erro ao carregar perfis'
    } finally {
      loading.value = false
    }
  }

  async function createUsuario(usuarioData) {
    loading.value = true
    error.value = null
    try {
      const payload = {
        ...usuarioData,
        situacao: usuarioData.situacao ? 1 : 0,
        freela: usuarioData.freela ? 1 : 0,
        site_parceiro: usuarioData.site_parceiro ? 1 : 0,
        datanascimento: usuarioData.datanascimento
          ? format(parse(usuarioData.datanascimento, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
          : '',
        dataadmissao: usuarioData.dataadmissao
          ? format(parse(usuarioData.dataadmissao, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
          : '',
        datademissao: usuarioData.datademissao
          ? format(parse(usuarioData.datademissao, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
          : '',
        senha: usuarioData.senha,
        senha_confirmation: usuarioData.senha,
        idperfil: usuarioData.perfil,
      }
      const response = await createUsuarioApi(payload)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao criar usuário'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateUsuario(id, usuarioData) {
    loading.value = true
    error.value = null
    try {
      const payload = {
        ...usuarioData,
        situacao: usuarioData.situacao ? 1 : 0,
        freela: usuarioData.freela ? 1 : 0,
        site_parceiro: usuarioData.site_parceiro ? 1 : 0,
        datanascimento: usuarioData.datanascimento
          ? format(parse(usuarioData.datanascimento, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
          : '',
        dataadmissao: usuarioData.dataadmissao
          ? format(parse(usuarioData.dataadmissao, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
          : '',
        datademissao: usuarioData.datademissao
          ? format(parse(usuarioData.datademissao, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
          : '',
        idperfil: usuarioData.perfil,
      }
      if (!payload.senha || payload.senha === '') {
        delete payload.senha
      } else {
        payload.senha_confirmation = payload.senha
      }
      await updateUsuarioApi(payload, id)
    } catch (err) {
      error.value = err.response?.data?.message || err.message || 'Erro ao atualizar usuário'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updatePaginaEstado(userId, paginaId, estado) {
    loading.value = true
    error.value = null
    try {
      const response = await useApi(USUARIO_ROTAS.ATUALIZA_ESTADO_PAGINA(userId, paginaId)).put(
        estado,
      )
      return response.data
    } catch (err) {
      error.value = err.message || 'Erro ao atualizar estado da página'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function deleteUsuario(id) {
    loading.value = true
    error.value = null
    try {
      await deleteUsuarioApi(id)
    } catch (err) {
      error.value = err.message || 'Erro ao excluir usuário'
    } finally {
      loading.value = false
    }
  }

  function clearUsuario() {
    usuario.value = null
  }

  return {
    data,
    dataUsuariosMeta,
    usuario,
    perfis,
    paginas,
    loading,
    error,
    fetchUsuarios,
    fetchUsuario,
    fetchPaginas,
    fetchPerfis,
    createUsuario,
    updateUsuario,
    updatePaginaEstado,
    deleteUsuario,
    clearUsuario,
  }
})
